import cx from 'classnames';
import { useEffect, useRef } from 'react';

export interface SentinelProps {
  /**
   * Callback function called when the sentinel enters or exits the viewport
   * @param isIntersecting - true when sentinel is visible, false when it exits viewport
   */
  onIntersectionChange: (isIntersecting: boolean) => void;

  /**
   * Additional CSS classes to apply to the sentinel element
   */
  className?: string;

  /**
   * Intersection Observer options
   */
  observerOptions?: IntersectionObserverInit;

  /**
   * Test ID for the sentinel element
   */
  testId?: string;
}

/**
 * A generic component that detects when its position scrolls in/out of the viewport.
 *
 * Uses an Intersection Observer to track when the sentinel element enters or exits
 * the viewport, calling the provided callback function with the intersection state.
 *
 * @param props - The component props
 * @returns {JSX.Element} A sentinel div for intersection observation
 */
export default function Sentinel({
  onIntersectionChange,
  className = '',
  observerOptions = {
    root: null,
    threshold: 0
  },
  testId
}: SentinelProps) {
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      onIntersectionChange(entry.isIntersecting);
    }, observerOptions);

    if (sentinelRef.current) {
      observer.observe(sentinelRef.current);
    }

    return () => observer.disconnect();
  }, [onIntersectionChange, observerOptions]);

  return (
    <div
      className={cx('u:h-0', className)}
      ref={sentinelRef}
      data-testid={testId}
    />
  );
}
